"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { PopText, Reveal, RevealGroup, RevealItem } from "@/components/motion/reveal";
import { GlassCard } from "@/components/ui/glass-card";
import { GradientText } from "@/components/ui/gradient-text";
import { Spotlight } from "@/components/ui/spotlight";
import { useAnalytics } from "@/hooks/use-analytics";
import { PREDICTIONS, type PredictionMarket } from "@/lib/landing-data";
import { APP_URL } from "@/lib/links";

function PredictionCard({ m }: { m: PredictionMarket }) {
  const analytics = useAnalytics();
  const no = 100 - m.yes;
  const track = (side: "Yes" | "No") =>
    analytics.trackButton(`Predict ${side}`, "Prediction markets", {
      market: m.question,
      button_url: APP_URL,
    });
  return (
    <RevealItem className="h-full">
      <GlassCard className="flex h-full flex-col gap-4 p-5">
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
            {m.category}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="size-1.5 rounded-full bg-emerald-400 motion-safe:animate-pulse" />
            Live
          </span>
        </div>

        <p className="text-base font-medium leading-snug">{m.question}</p>

        <div className="mt-auto">
          <div className="mb-1.5 flex items-center justify-between text-xs font-semibold">
            <span className="text-emerald-400">Yes {m.yes}%</span>
            <span className="text-rose-400">No {no}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-primary to-primary-glow"
              initial={{ width: 0 }}
              whileInView={{ width: `${m.yes}%` }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Link
            href={APP_URL}
            onClick={() => track("Yes")}
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 py-2 text-center text-sm font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/20"
          >
            Yes · {m.yes}¢
          </Link>
          <Link
            href={APP_URL}
            onClick={() => track("No")}
            className="rounded-lg border border-rose-500/30 bg-rose-500/10 py-2 text-center text-sm font-semibold text-rose-400 transition-colors hover:bg-rose-500/20"
          >
            No · {no}¢
          </Link>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Vol {m.volume}</span>
          <span>Ends {m.endsIn}</span>
        </div>
      </GlassCard>
    </RevealItem>
  );
}

export function Predictions() {
  return (
    <section id="predictions" className="relative overflow-hidden px-4 py-16 sm:py-20">
      <Spotlight fill="rgba(168,85,247,0.16)" className="right-0 top-1/4" />
      <Reveal className="mx-auto mb-12 max-w-2xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Prediction markets</p>
        <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
          <PopText>Trade </PopText>
          <PopText delay={0.18}>
            <GradientText>what happens next.</GradientText>
          </PopText>
        </h2>
        <p className="mx-auto mt-4 hidden max-w-xl text-lg text-muted-foreground md:block">
          Take a position on real-world outcomes — crypto, macro, tech. Back your conviction with
          a tap and exit any time the odds move.
        </p>
      </Reveal>

      <RevealGroup className="mx-auto grid max-w-4xl gap-5 sm:grid-cols-2">
        {PREDICTIONS.map((m) => (
          <PredictionCard key={m.id} m={m} />
        ))}
      </RevealGroup>
    </section>
  );
}
